/**
 * Learn Your Part Members Portal - JavaScript Entry Point
 */

// Lazy-load player components (only downloaded when <lyp-player> is used)
import('./player/player.mjs');

/**
 * TableView: Select all / deselect all checkboxes
 */
function initTableSelection() {
    document.querySelectorAll('[data-table-select-all]').forEach(selectAllCheckbox => {
        const tableId = selectAllCheckbox.dataset.tableSelectAll;

        selectAllCheckbox.addEventListener('change', () => {
            const rowCheckboxes = document.querySelectorAll(`[data-table-select-row="${tableId}"]`);
            rowCheckboxes.forEach(cb => {
                cb.checked = selectAllCheckbox.checked;
            });
        });
    });

    // Update "select all" when individual checkboxes change
    document.querySelectorAll('[data-table-select-row]').forEach(rowCheckbox => {
        rowCheckbox.addEventListener('change', () => {
            const tableId = rowCheckbox.dataset.tableSelectRow;
            const selectAllCheckbox = document.querySelector(`[data-table-select-all="${tableId}"]`);
            if (!selectAllCheckbox) return;

            const allRowCheckboxes = document.querySelectorAll(`[data-table-select-row="${tableId}"]`);
            const allChecked = Array.from(allRowCheckboxes).every(cb => cb.checked);
            const someChecked = Array.from(allRowCheckboxes).some(cb => cb.checked);

            selectAllCheckbox.checked = allChecked;
            selectAllCheckbox.indeterminate = someChecked && !allChecked;
        });
    });
}

/**
 * TableView: Search form submit on Enter
 */
function initTableSearch() {
    document.querySelectorAll('[data-table-search]').forEach(input => {
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                const form = input.closest('form');
                if (form) {
                    form.submit();
                } else {
                    // No form wrapper, update URL directly
                    const tableId = input.dataset.tableSearch;
                    const url = new URL(window.location.href);
                    if (input.value.trim()) {
                        url.searchParams.set(`${tableId}_q`, input.value.trim());
                    } else {
                        url.searchParams.delete(`${tableId}_q`);
                    }
                    url.searchParams.set(`${tableId}_page`, '1');
                    window.location.href = url.toString();
                }
            }
        });
    });

    document.querySelectorAll('.table-search-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            const input = btn.closest('.table-search')?.querySelector('[data-table-search]');
            if (!input) return;

            const tableId = input.dataset.tableSearch;
            const url = new URL(window.location.href);
            if (input.value.trim()) {
                url.searchParams.set(`${tableId}_q`, input.value.trim());
            } else {
                url.searchParams.delete(`${tableId}_q`);
            }
            url.searchParams.set(`${tableId}_page`, '1');
            window.location.href = url.toString();
        });
    });
}

/**
 * TableView: Clickable rows
 */
function initTableRowLinks() {
    document.querySelectorAll('tr[data-href]').forEach(row => {
        row.addEventListener('click', (e) => {
            // Don't navigate if clicking on a link, button, or input
            if (e.target.closest('a, button, input, .table-col-actions')) {
                return;
            }
            window.location.href = row.dataset.href;
        });
    });
}

/**
 * TableView: Sort and pagination links don't add to browser history
 */
function initTableNavigation() {
    // Sort links in table headers
    document.querySelectorAll('.table th a[href^="?"]').forEach(link => {
        link.addEventListener('click', (e) => {
            e.preventDefault();
            location.replace(link.href);
        });
    });

    // Pagination links
    document.querySelectorAll('.table-pagination a.page-link[href^="?"]').forEach(link => {
        link.addEventListener('click', (e) => {
            e.preventDefault();
            location.replace(link.href);
        });
    });
}

/**
 * Picker fields (htmx-powered server-side search)
 */
function initPickerFields() {
    // Open — click toggle to show panel, load content on first open
    document.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-picker-toggle]');
        if (!toggle) return;
        e.preventDefault();
        const field = toggle.closest('[data-picker-field]');
        const panel = field.querySelector('.picker-panel');
        if (!panel.hidden) { panel.hidden = true; return; }
        // Close other open panels
        document.querySelectorAll('[data-picker-field] .picker-panel:not([hidden])').forEach(p => p.hidden = true);
        panel.hidden = false;
        // Load content on first open
        if (!panel.dataset.loaded) {
            panel.dataset.loaded = '1';
            htmx.ajax('GET', field.dataset.pickerAction, { target: panel, swap: 'innerHTML' }).then(() => {
                panel.querySelector('.picker-search-input')?.focus();
            });
        } else {
            panel.querySelector('.picker-search-input')?.focus();
        }
    });

    // Select — click a result item (skip folder items, they use htmx)
    document.addEventListener('click', (e) => {
        const item = e.target.closest('.picker-item');
        if (!item || !item.dataset.pickerId) return;
        const field = item.closest('[data-picker-field]');
        if (!field) return;
        const hiddenInput = field.querySelector('input[type=hidden]');
        hiddenInput.value = item.dataset.pickerId;
        hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
        const display = field.querySelector('[data-picker-display]');
        display.textContent = item.dataset.pickerLabel;
        field.querySelector('.picker-display').classList.remove('picker-display-empty');
        const clearBtn = field.querySelector('[data-picker-clear]');
        if (clearBtn) clearBtn.hidden = false;
        // Update thumbnail for image pickers
        const thumbImg = field.querySelector('[data-picker-thumb]');
        if (thumbImg && item.dataset.pickerThumb) {
            thumbImg.src = item.dataset.pickerThumb;
            thumbImg.hidden = false;
        }
        field.querySelector('.picker-panel').hidden = true;
    });

    // Clear — click × button
    document.addEventListener('click', (e) => {
        const clearBtn = e.target.closest('[data-picker-clear]');
        if (!clearBtn) return;
        e.preventDefault();
        e.stopPropagation();
        const field = clearBtn.closest('[data-picker-field]');
        const hiddenInput = field.querySelector('input[type=hidden]');
        hiddenInput.value = '';
        hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
        field.querySelector('[data-picker-display]').textContent = '';
        field.querySelector('.picker-display').classList.add('picker-display-empty');
        // Hide thumbnail for image pickers
        const thumbImg = field.querySelector('[data-picker-thumb]');
        if (thumbImg) thumbImg.hidden = true;
        clearBtn.hidden = true;
    });

    // Close outside — click anywhere else
    document.addEventListener('click', (e) => {
        if (e.target.closest('[data-picker-field]')) return;
        document.querySelectorAll('[data-picker-field] .picker-panel:not([hidden])').forEach(p => p.hidden = true);
    });

    // Escape — close all panels
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            document.querySelectorAll('[data-picker-field] .picker-panel:not([hidden])').forEach(p => p.hidden = true);
        }
    });

    // Prevent Enter in picker search from submitting the host form
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && e.target.closest('.picker-panel')) {
            e.preventDefault();
        }
    });

    // Focus search input after htmx swaps inside picker panels (folder nav)
    document.addEventListener('htmx:afterSwap', (e) => {
        const panel = e.target.closest?.('.picker-panel');
        if (panel) {
            panel.querySelector('.picker-search-input')?.focus();
        }
    });
}

/**
 * Inline edit: submit on blur or Enter, revert on Escape
 */
function initInlineEdits() {
    document.querySelectorAll('.inline-edit input').forEach(input => {
        const original = input.value;

        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                input.blur();
            }
            if (e.key === 'Escape') {
                input.value = original;
                input.blur();
            }
        });

        input.addEventListener('blur', () => {
            if (input.value.trim() !== '' && input.value !== original) {
                input.closest('form').submit();
            }
        });
    });
}

/**
 * Dirty form warning: warn before leaving if any form has unsaved changes.
 * Forms can opt out with data-no-dirty-check.
 */
function initDirtyFormWarning() {
    let dirty = false;
    let submitting = false;

    /**
     * Whether typing in this field means there is work to lose.
     *
     * A GET form never holds unsaved work: submitting it is a navigation, and
     * nothing on the server changes. Treating one as dirty is how typing in
     * the header search produced a "you will lose changes" warning — and the
     * same was true of every search and filter bar in the admin, since those
     * are GET forms too. Opting each one out by attribute would have meant
     * remembering to, every time somebody adds a filter.
     */
    const holdsUnsavedWork = (target) => {
        if (target.closest('[data-no-dirty-check]')) return false;

        const form = target.closest('form');
        if (!form) return false;

        return (form.method || 'get').toLowerCase() === 'post';
    };

    document.addEventListener('input', (e) => {
        if (holdsUnsavedWork(e.target)) dirty = true;
    });

    document.addEventListener('change', (e) => {
        if (holdsUnsavedWork(e.target)) dirty = true;
    });

    // Clear dirty flag on form submit (covers declarative submits)
    document.addEventListener('submit', () => {
        dirty = false;
        submitting = true;
    });

    // Catch programmatic form.submit() which doesn't fire 'submit' event
    const origSubmit = HTMLFormElement.prototype.submit;
    HTMLFormElement.prototype.submit = function() {
        dirty = false;
        submitting = true;
        return origSubmit.call(this);
    };

    window.addEventListener('beforeunload', (e) => {
        if (dirty && !submitting) {
            e.preventDefault();
        }
    });
}

/**
 * Chunked file uploader. Used by /admin/pieces/{id}/parts/ to upload audio
 * and (especially) video files larger than typical post_max_size limits, with
 * progress and resume-on-retry. Server endpoints:
 *
 *   POST {init-url}    {filename, size, type, title}        -> {upload_id, chunk_size}
 *   POST {chunk-url}?upload_id=X&offset=Y  body=raw bytes   -> {received}
 *         (409 + {received} if offset doesn't match — we resync and continue)
 *   POST {commit-url}  {upload_id}                          -> {redirect}
 *   POST {abort-url}   {upload_id}                          -> {ok:true}
 */

/**
 * Read a JSON response from an upload endpoint, or throw a human message.
 *
 * The upload endpoints always answer with JSON — a success payload OR a
 * structured {error} with a non-2xx status. So if the body IS JSON we trust
 * it and return it, EVEN IF fetch reports res.redirected. That flag can be
 * set by benign infrastructure redirects and does not by itself mean the
 * upload failed; an earlier version treated any redirect as "session
 * expired", which masked real successes and, worse, disguised real server
 * errors (a 500 error page) as an auth problem.
 *
 * A non-JSON body means the request never reached the endpoint's own
 * response: either the auth gate bounced us to the HTML login page (session
 * really did expire) or the app rendered an HTML error page (e.g. a 500).
 * Tell those two apart so we don't cry "session expired" at every server
 * error — surface the actual status instead.
 */
async function readUploadJson(res) {
    const contentType = res.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
        return res.json();
    }
    if (res.status === 401 || res.redirected) {
        throw new Error('Your session has expired. Please reload the page and sign in again, then retry the upload.');
    }
    throw new Error(`Upload failed: the server returned an unexpected response (HTTP ${res.status}). Please try again; if the problem persists, contact support.`);
}

function initChunkedUploader() {
    document.querySelectorAll('form.chunked-uploader').forEach((form) => {
        const fileInput = form.querySelector('input[type="file"]');
        const titleInput = form.querySelector('input[name="title"]');
        const typeSelect = form.querySelector('select[name="type"]');
        const submitBtn = form.querySelector('button[type="submit"]');
        const progress = form.querySelector('.upload-progress');
        const progressFill = form.querySelector('.upload-progress-fill');
        const progressStatus = form.querySelector('.upload-progress-status');

        const setProgress = (pct, statusText) => {
            progress.hidden = false;
            progressFill.style.width = `${Math.min(100, Math.max(0, pct)).toFixed(1)}%`;
            if (statusText !== undefined) progressStatus.textContent = statusText;
        };

        const formatBytes = (n) => {
            if (n >= 1024 * 1024 * 1024) return (n / 1024 / 1024 / 1024).toFixed(2) + ' GB';
            if (n >= 1024 * 1024) return (n / 1024 / 1024).toFixed(1) + ' MB';
            if (n >= 1024) return (n / 1024).toFixed(1) + ' KB';
            return n + ' B';
        };

        form.addEventListener('submit', async (e) => {
            e.preventDefault();
            const file = fileInput.files && fileInput.files[0];
            if (!file) return;

            submitBtn.disabled = true;
            const initUrl = form.dataset.initUrl;
            const chunkUrl = form.dataset.chunkUrl;
            const commitUrl = form.dataset.commitUrl;
            const abortUrl = form.dataset.abortUrl;

            let uploadId = null;

            try {
                // 1) Init
                setProgress(0, 'Starting upload…');
                const initRes = await fetch(initUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json' },
                    body: new URLSearchParams({
                        filename: file.name,
                        size: String(file.size),
                        type: typeSelect.value,
                        title: titleInput.value || '',
                    }),
                });
                const initBody = await readUploadJson(initRes);
                if (!initRes.ok) throw new Error(initBody.error || 'Upload init failed');
                uploadId = initBody.upload_id;
                const chunkSize = initBody.chunk_size || 5 * 1024 * 1024;

                // 2) Stream chunks. Validate offset on each round-trip; if the
                //    server reports a different received count (e.g. partial
                //    write recovered), resume from there.
                let offset = 0;
                while (offset < file.size) {
                    const end = Math.min(offset + chunkSize, file.size);
                    const blob = file.slice(offset, end);

                    let attempt = 0;
                    while (true) {
                        attempt++;
                        const res = await fetch(`${chunkUrl}?upload_id=${encodeURIComponent(uploadId)}&offset=${offset}`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/octet-stream', 'Accept': 'application/json' },
                            body: blob,
                        });
                        // Non-JSON (auth redirect / HTML error page) throws a
                        // precise message here instead of a bare "session
                        // expired" on every hiccup.
                        const body = await readUploadJson(res);
                        if (res.status === 409 && typeof body.received === 'number') {
                            // Server has a different offset — sync to it and retry.
                            offset = body.received;
                            break;
                        }
                        if (!res.ok) {
                            if (attempt < 3) { await new Promise(r => setTimeout(r, 500 * attempt)); continue; }
                            throw new Error(body.error || `Chunk upload failed (HTTP ${res.status})`);
                        }
                        offset = body.received;
                        break;
                    }

                    const pct = (offset / file.size) * 100;
                    setProgress(pct, `${formatBytes(offset)} / ${formatBytes(file.size)} (${pct.toFixed(1)}%)`);
                }

                // 3) Commit
                setProgress(100, 'Finalizing…');
                const commitRes = await fetch(commitUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json' },
                    body: new URLSearchParams({ upload_id: uploadId }),
                });
                const commitBody = await readUploadJson(commitRes);
                if (!commitRes.ok) throw new Error(commitBody.error || 'Commit failed');

                setProgress(100, 'Done.');
                window.location.href = commitBody.redirect;
            } catch (err) {
                console.error(err);
                progressStatus.textContent = `Error: ${err.message || err}`;
                if (uploadId) {
                    // Best-effort cleanup; ignore failures.
                    fetch(abortUrl, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: new URLSearchParams({ upload_id: uploadId }),
                    }).catch(() => {});
                }
                submitBtn.disabled = false;
            }
        });
    });
}

/**
 * Mobile sidebar: hamburger toggle, backdrop dismissal, Escape to close.
 */
function initMobileNav() {
    const toggle = document.querySelector('[data-sidebar-toggle]');
    if (!toggle) return;

    const close = () => {
        document.body.classList.remove('sidebar-open');
        toggle.setAttribute('aria-expanded', 'false');
    };

    const open = () => {
        document.body.classList.add('sidebar-open');
        toggle.setAttribute('aria-expanded', 'true');
    };

    toggle.addEventListener('click', () => {
        document.body.classList.contains('sidebar-open') ? close() : open();
    });

    document.addEventListener('click', (e) => {
        if (e.target.closest('[data-sidebar-close]')) {
            e.preventDefault();
            close();
        }
    });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') close();
    });

    // Nav-link tap closes the drawer. The new page would render with the
    // drawer collapsed anyway, but doing it immediately makes the tap
    // feel responsive on slow networks.
    document.querySelectorAll('.lyp-sidebar-nav a').forEach(a => {
        a.addEventListener('click', close);
    });
}

/**
 * Favorite hearts: any element with [data-favorite-toggle] and a
 * [data-url] POSTs to the toggle endpoint and flips its icon. Works
 * both inside card links (click must not navigate) and as a
 * standalone button on the play page.
 */
function initFavoriteToggles() {
    document.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-favorite-toggle]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        if (btn.dataset.busy) return;
        btn.dataset.busy = '1';
        try {
            const r = await fetch(btn.dataset.url, {
                method: 'POST',
                headers: { 'Accept': 'application/json' },
                credentials: 'same-origin',
            });
            if (!r.ok) return;
            const body = await r.json();
            const fav = !!body.favorite;
            btn.classList.toggle('is-favorite', fav);
            btn.setAttribute('aria-pressed', fav ? 'true' : 'false');
            const icon = btn.querySelector('i');
            if (icon) {
                icon.classList.toggle('fa-solid', fav);
                icon.classList.toggle('fa-light', !fav);
            }
            const label = btn.querySelector('[data-favorite-label]');
            if (label) label.textContent = fav ? 'Favorited' : 'Add to favorites';
        } catch (err) {
            // Network hiccup — leave the heart as it was.
        } finally {
            delete btn.dataset.busy;
        }
    });
}

/**
 * "Add to projects" menu on piece cards. The [data-projects-toggle] button
 * opens a floating checklist of the user's projects; checking a box adds the
 * piece to that project, unchecking removes it. The menu is appended to
 * <body> and positioned fixed so it escapes the card's overflow:hidden.
 */
function initProjectMenus() {
    let openMenu = null;
    let openButton = null;

    function closeMenu() {
        if (openMenu) openMenu.remove();
        if (openButton) openButton.setAttribute('aria-expanded', 'false');
        openMenu = null;
        openButton = null;
    }

    function positionMenu(menu, button) {
        const r = button.getBoundingClientRect();
        const width = menu.offsetWidth || 260;
        const height = menu.offsetHeight || 0;
        // Right-align the menu to the button; keep it on-screen.
        let left = Math.min(r.right - width, window.innerWidth - width - 8);
        left = Math.max(8, left);
        let top = r.bottom + 6;
        if (top + height > window.innerHeight - 8 && r.top - height - 6 > 8) {
            top = r.top - height - 6; // flip above when it would overflow
        }
        menu.style.left = `${left}px`;
        menu.style.top = `${top}px`;
    }

    function renderItems(menu, button, projects) {
        const list = menu.querySelector('.ppm-list');
        list.innerHTML = '';

        if (!projects.length) {
            const empty = document.createElement('div');
            empty.className = 'ppm-empty';
            empty.textContent = 'You have no projects yet.';
            list.appendChild(empty);
        }

        projects.forEach(p => {
            const item = document.createElement('label');
            item.className = 'ppm-item';

            const cb = document.createElement('input');
            cb.type = 'checkbox';
            cb.checked = !!p.checked;

            const text = document.createElement('span');
            text.className = 'ppm-item-text';
            const nameEl = document.createElement('span');
            nameEl.textContent = p.name;
            text.appendChild(nameEl);
            if (p.org_name) {
                const org = document.createElement('span');
                org.className = 'ppm-item-org';
                org.textContent = p.org_name;
                text.appendChild(org);
            }

            cb.addEventListener('change', async () => {
                if (item.dataset.busy) return;
                item.dataset.busy = '1';
                const want = cb.checked;
                try {
                    const res = await fetch(`${button.dataset.toggleBase}${p.id}/`, {
                        method: 'POST',
                        headers: { 'Accept': 'application/json' },
                        credentials: 'same-origin',
                    });
                    if (!res.ok) throw new Error('toggle failed');
                    const body = await res.json();
                    cb.checked = !!body.checked;
                } catch (err) {
                    cb.checked = !want; // revert on failure
                } finally {
                    delete item.dataset.busy;
                }
            });

            item.appendChild(cb);
            item.appendChild(text);
            list.appendChild(item);
        });

        positionMenu(menu, button);
    }

    async function openFor(button) {
        closeMenu();

        const menu = document.createElement('div');
        menu.className = 'piece-projects-menu';
        menu.innerHTML =
            '<div class="ppm-header">Add to projects</div>' +
            '<div class="ppm-list"><div class="ppm-loading">Loading…</div></div>';

        if (button.dataset.newUrl) {
            const footer = document.createElement('div');
            footer.className = 'ppm-footer';
            const a = document.createElement('a');
            a.href = button.dataset.newUrl;
            a.innerHTML = '<i class="fa-light fa-plus"></i> New project';
            footer.appendChild(a);
            menu.appendChild(footer);
        }

        // Clicks inside the menu must not bubble to the card link or the
        // document-level "close on outside click" handler.
        menu.addEventListener('click', e => e.stopPropagation());

        document.body.appendChild(menu);
        openMenu = menu;
        openButton = button;
        button.setAttribute('aria-expanded', 'true');
        positionMenu(menu, button);

        try {
            const res = await fetch(button.dataset.listUrl, {
                headers: { 'Accept': 'application/json' },
                credentials: 'same-origin',
            });
            if (!res.ok) throw new Error('list failed');
            const body = await res.json();
            if (openMenu !== menu) return; // closed while loading
            renderItems(menu, button, body.projects || []);
        } catch (err) {
            const list = menu.querySelector('.ppm-list');
            if (list) list.innerHTML = '<div class="ppm-empty">Could not load projects.</div>';
        }
    }

    document.addEventListener('click', (e) => {
        const button = e.target.closest('[data-projects-toggle]');
        if (button) {
            e.preventDefault();
            e.stopPropagation();
            if (openButton === button) {
                closeMenu();
            } else {
                openFor(button);
            }
            return;
        }
        if (openMenu) closeMenu(); // click anywhere else closes it
    });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeMenu();
    });

    window.addEventListener('resize', closeMenu);
    window.addEventListener('scroll', closeMenu, true);
}

/**
 * "Add to project" form on the play page: the POST target depends on
 * the selected project, so set the form action at submit time.
 */
function initAddToProject() {
    document.querySelectorAll('[data-add-to-project]').forEach(form => {
        form.addEventListener('submit', (e) => {
            const select = form.querySelector('select[name="project_id"]');
            const projectId = select && select.value;
            if (!projectId) {
                e.preventDefault();
                return;
            }
            form.action = `${window.BASE_URL}/projects/${projectId}/pieces/`;
        });
    });
}

/**
 * Playback tracking (TONO reporting + "recently played").
 *
 * Efficiency contract: never chatty. Seconds are accumulated locally
 * from the player's statechange events; a diff is flushed at most once
 * per minute while audio rolls, plus a final keepalive fetch on page
 * hide/unload. Pause/play toggling by itself sends nothing.
 */
function initPlaybackTracker() {
    const player = document.querySelector('lyp-player');
    if (!player) return;
    const src = player.getAttribute('src') || '';
    if (!src.endsWith('meta.json')) return;
    const endpoint = src.replace(/meta\.json$/, 'playback/');

    const session = (crypto.randomUUID && crypto.randomUUID())
        || `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    // Two independent measures, both reported:
    //
    //   wallclock — seconds the player spent in the playing state. What we
    //               have always recorded.
    //   heard     — DISTINCT seconds of this work's timeline that actually
    //               reached the listener during this session.
    //
    // They diverge sharply for rehearsal, which is the whole point. Looping
    // one 30-second verse for 25 minutes is 1500 wallclock seconds but 30
    // seconds of music heard, and under TONO's 5 øre per streamed
    // music-minute the difference is paid in cash. See
    // docs/playback-measurement.md for the methodology.
    const heard = new Set();       // integer second offsets covered this session
    let heardReported = 0;

    let playingSince = null;       // performance.now() when playback last started
    let accumulated = 0;           // wall-clock seconds not yet flushed
    let first = true;              // first flush of this session bumps play_count
    let flushTimer = null;
    let sampleTimer = null;

    /**
     * Is audio genuinely reaching the listener right now?
     *
     * Not the same as state === 'playing'. Dragging the scrubber holds the
     * state at playing while setting playbackRate to 0, and a user who has
     * muted every part hears nothing at all. Counting either would be
     * charging ourselves for silence.
     */
    const isAudible = () => {
        if (player.state !== 'playing') return false;
        if (player.mouseScrubbing && player.mouseScrubbing.isDragging) return false;
        const tracks = player.tracks ? Object.values(player.tracks) : [];
        if (tracks.length && tracks.every((t) => t.muted)) return false;
        return true;
    };

    // Sampling at 4 Hz marks every second of ordinary playback while staying
    // cheap. A second is only ever recorded once, so the rate cannot inflate
    // the total.
    const sample = () => {
        if (!isAudible()) return;
        const t = player.currentTime;
        if (typeof t === 'number' && isFinite(t) && t >= 0) {
            heard.add(Math.floor(t));
        }
    };

    /**
     * The speed the piece is being played at, 1 when the player has no tempo
     * control or it is at normal speed. Read at flush time rather than
     * accumulated per-sample: tempo changes are rare and a flush covers at most
     * a minute, so the error from a mid-flush change is bounded by that minute
     * and is not worth a second accumulator.
     */
    const playbackTempo = () => {
        const t = Number(player.tempo);
        return Number.isFinite(t) && t > 0 ? t : 1;
    };

    const settle = () => {
        if (playingSince !== null) {
            const now = performance.now();
            accumulated += (now - playingSince) / 1000;
            playingSince = now;
        }
    };

    const flush = (useBeacon) => {
        settle();

        // Wall-clock seconds are seconds of MUSIC, not seconds of listening.
        //
        // At tempo 0.75 a passage takes a third longer to play, so a chorister
        // practising slowly would report a third more streamed music than one
        // at full speed — and we would pay TONO a third more for the same
        // music. Dividing by the rate converts back to the work's own timeline,
        // which is what a royalty is owed on.
        //
        // `heard` needs no such correction: it is a set of integer offsets into
        // the piece, and those are already in the work's timeline whatever
        // speed it was played at.
        const wallclock = Math.floor(accumulated * playbackTempo());
        const newlyHeard = heard.size - heardReported;

        // Nothing new on either measure: no reason to send anything.
        if (wallclock < 1 && newlyHeard < 1) return;

        // Give back the un-scaled equivalent of what was just reported, so the
        // residue stays in wall-clock terms like the rest of `accumulated`.
        accumulated -= wallclock / playbackTempo();
        heardReported += newlyHeard;

        const payload = JSON.stringify({ seconds: wallclock, heard: newlyHeard, session, first });
        first = false;

        // Always fetch, never sendBeacon — even on pagehide. A beacon cannot
        // carry headers, so it went out without the X-CSRF-Token the page's
        // fetch wrapper adds, and CsrfMiddleware refused it: from 28 July to
        // 4 November every final flush was a 403, which lost every listen
        // shorter than a minute and the tail of every longer one. keepalive is
        // what lets this request outlive the page, which is all the beacon
        // was for. `useBeacon` still marks the unload flush for the reader.
        void useBeacon;
        fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: payload,
            credentials: 'same-origin',
            keepalive: true,
        }).catch(() => {});
    };

    // When the player says it is playing and nothing is heard, say what it saw.
    //
    // Jan, 14 October, on an iPad with Safari 26.6: the player sat in 'playing'
    // for minutes with not one second heard, and resuming the AudioContext in
    // the click did not change that. The play log can show THAT it happens; it
    // cannot show WHY — a suspended or interrupted context, a clock that does
    // not move, every voice muted, a video that never started. One report per
    // stalled start, sent at once rather than on unload, so it arrives even if
    // the page is closed in frustration.
    let stallTimer = null;
    let stallReported = false;
    const reportStall = () => {
        if (stallReported || player.state !== 'playing' || heard.size > 0) return;
        stallReported = true;
        const ctx = player.audioContext;
        const tracks = player.tracks ? Object.values(player.tracks) : [];
        const video = player.shadowRoot && player.shadowRoot.querySelector('video');
        const ctxTimeBefore = ctx ? ctx.currentTime : null;
        // Read the clock twice, half a second apart: the question is whether it moves.
        setTimeout(() => {
            const diag = {
                ua: navigator.userAgent,
                touchPoints: navigator.maxTouchPoints || 0,
                playerState: player.state,
                position: Number(player.currentTime),
                tempo: Number(player.tempo) || 1,
                // A remembered loop plays the same instant forever if it sits
                // in a silent intro — the same signature as a frozen engine.
                loop: player.loopStart != null ? {
                    start: player.loopStart, end: player.loopEnd, mode: player.loopMode,
                } : null,
                ctxState: ctx ? ctx.state : 'none',
                ctxTimeBefore,
                ctxTimeAfter: ctx ? ctx.currentTime : null,
                sampleRate: ctx ? ctx.sampleRate : null,
                tracks: tracks.length,
                muted: tracks.filter((t) => t.muted).length,
                buffered: tracks.filter((t) => t.audioBuffer).length,
                video: video ? {
                    paused: video.paused, readyState: video.readyState,
                    time: video.currentTime, muted: video.muted,
                } : null,
            };
            fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ diag }),
                credentials: 'same-origin',
                keepalive: true,
            }).catch(() => {});
        }, 500);
    };

    // The player's own watchdog: the video claims to play and does not move.
    // Same endpoint and log as a stalled start, tagged so they can be told apart.
    player.addEventListener('videostuck', (e) => {
        fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ diag: {
                kind: 'video-stuck',
                ua: navigator.userAgent,
                touchPoints: navigator.maxTouchPoints || 0,
                ...e.detail,
            } }),
            credentials: 'same-origin',
            keepalive: true,
        }).catch(() => {});
    });

    player.addEventListener('statechange', () => {
        clearTimeout(stallTimer);
        if (player.state === 'playing') {
            stallTimer = setTimeout(reportStall, 5000);
        }
    });

    player.addEventListener('statechange', () => {
        if (player.state === 'playing') {
            if (playingSince === null) playingSince = performance.now();
            if (!flushTimer) flushTimer = setInterval(() => flush(false), 60000);
            if (!sampleTimer) sampleTimer = setInterval(sample, 250);
        } else if (playingSince !== null) {
            accumulated += (performance.now() - playingSince) / 1000;
            playingSince = null;
        }
    });

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flush(true);
    });
    window.addEventListener('pagehide', () => flush(true));
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
    initTableSelection();
    initTableSearch();
    initTableRowLinks();
    initTableNavigation();
    initPickerFields();
    initInlineEdits();
    initDirtyFormWarning();
    initChunkedUploader();
    initMobileNav();
    initFavoriteToggles();
    initProjectMenus();
    initAddToProject();
    initPlaybackTracker();
});
